import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

interface User {
  _id: string;
  email?: string; // Optional since emails are excluded from API response for security
  username: string;
  firstName?: string;
  lastName?: string;
  nativeLanguage?: string;
  learningLevel?: string;
  subscription: {
    type: 'free' | 'premium' | 'lifetime';
    startDate: string;
    endDate?: string;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
  };
  stats: {
    totalWordsLearned: number;
    currentStreak: number;
    longestStreak: number;
    totalStudyTime: number;
    xpPoints: number;
    level: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  nextCursor: string | null;
  hasMore: boolean;
  total: number;
}

interface UseUsersOptions {
  limit?: number;
  sort?: string;
}

interface UseUsersReturn {
  users: User[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  pagination: PaginationInfo | null;
}

export const useUsers = (options: UseUsersOptions = {}): UseUsersReturn => {
  const { limit = 10, sort = '_id' } = options;
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchUsers = useCallback(async (cursor?: string | null) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        sort
      });
      
      if (cursor) {
        params.append('cursor', cursor);
      }
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      
      if (data.success) {
        if (cursor) {
          // Append to existing users for pagination
          setUsers(prev => [...prev, ...data.data]);
        } else {
          // Replace users for refresh
          setUsers(data.data);
        }
        
        setPagination(data.pagination);
        setNextCursor(data.pagination.nextCursor);
      } else {
        throw new Error(data.message || 'Failed to fetch users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [limit, sort]);

  const loadMore = useCallback(async () => {
    if (nextCursor && !loading) {
      await fetchUsers(nextCursor);
    }
  }, [nextCursor, loading, fetchUsers]);

  const refresh = useCallback(async () => {
    setNextCursor(null);
    await fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    hasMore: pagination?.hasMore || false,
    loadMore,
    refresh,
    pagination
  };
};

// Hook for streaming all users (for admin purposes)
export const useUsersStream = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users/stream`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  return {
    users,
    loading,
    error,
    refresh: fetchAllUsers
  };
}; 